import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { string } from "zod";

import { Button } from "@/components/Button/Button";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { BaggageOptionCard } from "@/features/flights/components/flight-purchase/additional-services/BaggageOptionCard";
import { IncludedBaggage } from "@/features/flights/components/flight-purchase/additional-services/IncludedBaggage";
import { RouteCard } from "@/features/flights/components/flight-purchase/additional-services/RouteCard";
import {
  getRoundTripSegmentDirection,
  hasOnlyOneTypeOfAdditionalServices,
  mapServicesToRoutes,
} from "@/features/flights/components/flight-purchase/additional-services/utils";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import type { AdditionalServiceData } from "@/features/flights/types/types";
import { getAvailableServices } from "@/features/flights/utils/additionalServices";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES } from "@/utils/constants";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/_flight-offer/offer/$id/step/2/additional-services/baggage-selection",
)({
  validateSearch: (search: Record<string, unknown>) => {
    return { travellerId: string().parse(search.travellerId) };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { travellerId } = Route.useSearch();
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();
  const intl = useIntl();

  const { selectedFlightPayload, setTravellerServices, getTravellerServices } =
    useFlightSelectionPayloadContext();
  const { getTravellerIndex } = useTravellersSelectionContext();
  const { data: singleFlightDetails } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );
  const { data: travellers } = useQuery(travellersQueryOptions());

  const [selectedServices, setSelectedServices] = useState<
    AdditionalServiceData[]
  >(getTravellerServices(travellerId));
  // setState is being used despite not setter because we need this value to be set only on first render
  // to avoid reordering list when user select/deselect services
  const [selectedAtStart] = useState<Set<string> | null>(
    () => new Set(selectedServices.map((s) => s.service.bookingCode)),
  );

  const traveller = travellers?.find((t) => t.id === travellerId);

  const [routes, isSingleTypeOfAdditionalService] = useMemo(() => {
    if (!traveller || !singleFlightDetails) return [undefined, false];

    const services = getAvailableServices({
      traveller,
      travellerIndex: getTravellerIndex(travellerId),
      optionalServices: singleFlightDetails.optionalServices,
      mandatoryServices: singleFlightDetails.mandatoryServices,
    });

    const isSingleTypeOfAdditionalService =
      hasOnlyOneTypeOfAdditionalServices(services);

    return [
      mapServicesToRoutes(
        services,
        SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES,
      ),
      isSingleTypeOfAdditionalService,
    ];
  }, [getTravellerIndex, singleFlightDetails, traveller, travellerId]);

  const serviceRouteMap = useMemo(() => {
    if (!routes || !selectedAtStart) return;

    return Object.fromEntries(
      Object.entries(routes).map(([route, services]) => [
        route,
        [...services].sort(
          (a, b) =>
            Number(selectedAtStart.has(b.service.bookingCode)) -
            Number(selectedAtStart.has(a.service.bookingCode)),
        ),
      ]),
    );
  }, [routes, selectedAtStart]);

  const handleToggleBaggage = (selectedService: AdditionalServiceData) => {
    setSelectedServices((prev) => {
      const newSelectedServices = prev.filter(
        ({ service }) =>
          service.bookingCode !== selectedService.service.bookingCode ||
          service.selectedOptionCode !==
            selectedService.service.selectedOptionCode,
      );

      if (newSelectedServices.length < prev.length) {
        return newSelectedServices;
      }

      return [...prev, selectedService];
    });
  };

  if (!serviceRouteMap || !singleFlightDetails)
    return (
      <PageLoader>
        <AnimatedLoader icon="airplane" />
      </PageLoader>
    );

  return (
    <div className="px-main">
      <div className="mb-4 text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Add extra baggage",
          description: "travellers.addExtraBaggage",
        })}
      </div>
      <div className="mb-[calc(var(--drawer-height,0)-var(--drawer-bottom-padding,0))] flex flex-col gap-4">
        {Object.entries(serviceRouteMap).map(([route, services]) => {
          const routeCodes = route.split(" - ");
          const segmentDirection = getRoundTripSegmentDirection(
            singleFlightDetails.flights[0],
            singleFlightDetails.flights[1],
            routeCodes[0],
            routeCodes[1],
          );
          return (
            <RouteCard
              key={route}
              routeCodes={routeCodes}
              services={services}
              flightDetails={singleFlightDetails}
            >
              {segmentDirection ? (
                <IncludedBaggage
                  data={
                    segmentDirection === "departure"
                      ? singleFlightDetails.outboundBaggage
                      : singleFlightDetails.returnBaggage
                  }
                />
              ) : null}

              <div className="font-medium text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Purchase additional baggage",
                  description: "travellers.purchaseAdditionalBaggage",
                })}
              </div>
              {services.map((service) =>
                service.service.options.map((option) => {
                  const isSelected = selectedServices.some(
                    (selectedService) =>
                      selectedService.service.bookingCode ===
                        service.service.bookingCode &&
                      selectedService.service.selectedOptionCode ===
                        option.code,
                  );

                  return (
                    <BaggageOptionCard
                      key={option.code}
                      label={option.description}
                      price={option.price}
                      selected={isSelected}
                      onClick={() =>
                        handleToggleBaggage({
                          service: {
                            bookingCode: service.service.bookingCode,
                            selectedOptionCode: option.code,
                          },
                          mandatory: service.mandatory,
                        })
                      }
                    />
                  );
                }),
              )}
            </RouteCard>
          );
        })}
      </div>
      <PersistentDrawer>
        <Button
          fullWidth
          variant="contained"
          size="medium"
          onClick={() => {
            setTravellerServices(
              travellerId,
              selectedServices.map((s) => ({
                mandatory: s.mandatory,
                service: {
                  bookingCode: s.service.bookingCode,
                  selectedOptionCode: s.service.selectedOptionCode,
                },
              })),
            );
            showSnackbar({
              content: intl.formatMessage({
                defaultMessage: "Baggages added to the flight",
                description: "travellers.baggagesAddedToTheFlight",
              }),
            });
            if (isSingleTypeOfAdditionalService) {
              navigate({
                to: "/flights/offer/$id/step/2",
                params: { id },
              });
            } else {
              navigate({
                to: "/flights/offer/$id/step/2/additional-services",
                params: { id },
                search: { travellerId },
              });
            }
          }}
        >
          {intl.formatMessage({
            defaultMessage: "Confirm",
            description: "confirm",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
